import { Label } from '@optools/components/ui/label';
import { Switch } from '@optools/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import {
  ProgramSource,
  Region,
  SourceType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import type { BroadcastSourceChangeArgs } from '@optools/tools/btc-tools/hooks/useBroadcastSourceUpdate';
import { Circle, CircleHelp } from 'lucide-react';

interface BaseBroadcastSourceSwitchProps {
  active: boolean;
  broadcastSourceValue: ProgramSource;
  disabled?: boolean;
  hideHeader?: boolean;
  label?: string;
  loading: boolean;
  onBroadcastSourceChange?: (args: BroadcastSourceChangeArgs, region?: Region) => void;
  onProgramSourceChange?: (value: boolean, region?: Region) => void;
  region?: Region;
}

type BroadcastSourceSwitchProps = BaseBroadcastSourceSwitchProps &
  (
    | {
        broadcastSourceType: SourceType;
        onBroadcastSourceChange: (args: BroadcastSourceChangeArgs, region?: Region) => void;
        onProgramSourceChange?: never;
      }
    | {
        broadcastSourceType: 'PROGRAM';
        onProgramSourceChange: (value: boolean, region?: Region) => void;
        onBroadcastSourceChange?: never;
      }
  );

export function BroadcastSourceSwitch({
  active,
  broadcastSourceType,
  broadcastSourceValue: programSourceValue,
  disabled = false,
  hideHeader = false,
  label,
  loading,
  onBroadcastSourceChange,
  onProgramSourceChange,
  region,
}: BroadcastSourceSwitchProps) {
  return (
    <div
      className={cn('flex flex-col items-start gap-4 px-3 py-2', {
        'rounded-md bg-accent': active,
      })}>
      {!hideHeader && (
        <div className="flex w-full items-center justify-between gap-4">
          <Label className="text-sm font-bold">
            {label && label}
            {!disabled && (
              <ActiveIndicator
                active={active}
                data-testid={`${broadcastSourceType}-source-active-indicator`}
              />
            )}
          </Label>
          {!disabled && (
            <>
              {broadcastSourceType === 'PROGRAM' ? (
                <ProgramSourceHelp />
              ) : (
                <BroadcastSourceHelp active={active} />
              )}
            </>
          )}
        </div>
      )}
      <div className="flex h-full items-center gap-2 text-xs">
        <Switch
          aria-label={`${region ? `${region.toLocaleLowerCase()} ` : ''}${broadcastSourceType.toLocaleLowerCase()} source`}
          checked={programSourceValue === ProgramSource.BACKHAUL}
          className="order-2 data-unchecked:bg-primary! data-unchecked:**:data-[slot=switch-thumb]:bg-primary-foreground!"
          disabled={disabled || loading}
          id={`source-switch-${broadcastSourceType}${region ? `-${region}` : ''}`}
          size="sm"
          onCheckedChange={(value) =>
            broadcastSourceType === 'PROGRAM'
              ? onProgramSourceChange?.(value, region)
              : onBroadcastSourceChange?.({
                  active,
                  broadcastSourceType,
                  checked: value,
                  region,
                })
          }
        />
        <Label
          className="order-1 cursor-pointer text-xs font-normal peer-disabled:opacity-50 peer-data-[state=unchecked]:font-bold!"
          htmlFor={`source-switch-${broadcastSourceType}${region ? `-${region}` : ''}`}>
          Fronthaul
        </Label>
        <Label
          className="order-3 cursor-pointer text-xs font-normal peer-disabled:opacity-50 peer-data-[state=checked]:font-bold!"
          htmlFor={`source-switch-${broadcastSourceType}${region ? `-${region}` : ''}`}>
          Backhaul
        </Label>
      </div>
    </div>
  );
}

export function ActiveIndicator({
  active,
  'data-testid': dataTestId,
}: {
  active: boolean;
  'data-testid'?: string;
}) {
  return active ? (
    <span className="relative flex size-2" data-testid={dataTestId}>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
      <span className="relative inline-flex size-2 rounded-full bg-green-500"></span>
    </span>
  ) : (
    <Circle className="size-2.5 text-muted-foreground" />
  );
}

export function BroadcastSourceHelp({ active }: { active: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <CircleHelp className="size-4" role="presentation" />
      </TooltipTrigger>
      <TooltipContent align="start" className="p-2 text-xs">
        {active ? (
          <div className="space-y-2">
            <p className="flex items-center gap-2 font-bold">
              <ActiveIndicator active={true} />
              Active
            </p>
            <p>
              Since we are currently in this game state, this active toggle will route
              fronthaul/backhaul to program source
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 font-bold">
              <ActiveIndicator active={false} />
              Inactive
            </p>
            <p>
              On transition to this game state, the configured fronthaul/backhaul source will be
              routed to program source
            </p>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function ProgramSourceHelp() {
  return (
    <Tooltip>
      <TooltipTrigger>
        <CircleHelp className="size-4" role="presentation" />
      </TooltipTrigger>
      <TooltipContent align="start" className="p-2 text-xs">
        This active toggle will route fronthaul/backhaul to program source until we enter pre-game,
        game, or post-game
      </TooltipContent>
    </Tooltip>
  );
}
